import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ShieldCheck, CheckCircle2, ChevronRight } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import Avatar from '../../components/Avatar';
import { adminApi, AdminUser } from '../../api';

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (query: string) => {
    setLoading(true);
    try { setUsers(await adminApi.listUsers(query)); }
    catch { /* handled by empty state */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
  }, [q, load]);

  return (
    <AdminLayout>
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search golfers by name or email"
          className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 text-sm"
        />
      </div>

      {loading ? (
        <div className="text-green-200/80 py-10 text-center">Loading…</div>
      ) : users.length === 0 ? (
        <div className="bg-white rounded-2xl p-8 text-center text-gray-500">No golfers found.</div>
      ) : (
        <div className="space-y-2">
          {users.map((u) => (
            <Link
              to={`/admin/users/${u.id}`}
              key={u.id}
              className="bg-white rounded-2xl p-3 shadow-sm border border-gray-100 flex items-center gap-3 hover:border-emerald-300 transition"
            >
              <Avatar url={(u as any).avatar_url} name={u.full_name} size="w-10 h-10" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <p className="font-semibold text-gray-900 truncate">{u.full_name}</p>
                  {u.is_admin && <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 shrink-0" />}
                  {u.is_verified && <CheckCircle2 className="h-3.5 w-3.5 text-blue-500 shrink-0" />}
                </div>
                <p className="text-xs text-gray-500 truncate">{u.email}</p>
                <p className="text-xs text-gray-400">{u.games_count} games · HCP {u.handicap ?? 'N/A'}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-bold text-gray-900">R{u.balance_zar.toFixed(2)}</p>
                <p className="text-xs text-gray-400">balance</p>
              </div>
              <ChevronRight className="h-4 w-4 text-gray-300 shrink-0" />
            </Link>
          ))}
        </div>
      )}
      <p className="text-xs text-green-300/60 mt-3 text-center">Showing up to 50 most-recent matches.</p>
    </AdminLayout>
  );
};

export default AdminUsers;
